import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing authorization...');

  // Extract provider from path: /auth/google/callback -> google
  const provider = location.pathname.split('/')[2];

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');

      if (error) {
        setStatus('error');
        setMessage(`Authorization denied: ${error}`);
        toast.error('Authorization was denied');
        setTimeout(() => navigate('/dashboard'), 3000);
        return;
      }

      if (!code || !state) {
        setStatus('error');
        setMessage('Missing authorization code or state');
        toast.error('Invalid callback parameters');
        setTimeout(() => navigate('/dashboard'), 3000);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        
        // Determine the callback endpoint based on provider
        let callbackEndpoint = '';
        switch (provider) {
          case 'google':
            callbackEndpoint = '/api/integrations/google/callback';
            break;
          case 'slack':
            callbackEndpoint = '/api/integrations/slack/callback';
            break;
          case 'discord':
            callbackEndpoint = '/api/integrations/discord/callback';
            break;
          default:
            throw new Error(`Unknown provider: ${provider}`);
        }

        const response = await axios.post(
          `${API}${callbackEndpoint}`,
          { code, state },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          setStatus('success');
          setMessage(response.data.message || 'Connected successfully!');
          toast.success(response.data.message || 'Integration connected successfully!');
          
          // Redirect to dashboard with app hub tab active
          setTimeout(() => {
            navigate('/dashboard?tab=app-hub');
          }, 2000);
        } else {
          throw new Error(response.data.message || 'Connection failed');
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        setMessage(error.response?.data?.detail || error.message || 'Failed to complete authorization');
        toast.error('Failed to connect integration');
        setTimeout(() => navigate('/dashboard?tab=app-hub'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, provider, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-violet-900 to-slate-900 flex items-center justify-center">
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-md w-full mx-4 text-center">
        {status === 'processing' && (
          <>
            <Loader2 className="h-16 w-16 text-violet-400 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Connecting...</h2>
            <p className="text-slate-300">{message}</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Connected!</h2>
            <p className="text-slate-300">{message}</p>
            <p className="text-sm text-slate-400 mt-4">Redirecting to dashboard...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <XCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Connection Failed</h2>
            <p className="text-slate-300">{message}</p>
            <p className="text-sm text-slate-400 mt-4">Redirecting to dashboard...</p>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;
